import { numeric } from '@regle/rules';
import { useI18n } from 'vue-i18n';
import type { IFilterActionsModel } from '@/types/composables/filter-shared/useFilterActions';
import type { IFilterDetailsModel } from '@/types/composables/filter-shared/useFilterDetails';
import { computed, type UnwrapRef } from 'vue';
import useFilterService from '@/composables/apiServices/useFilterService';
import type {
  ISettingsFilterItemForm,
  ISettingsParsedFilterItem
} from '@/types/services/filter.service';
import { isValidUrlFormat } from '@/helpers/urlFormat';
import { useRegleConfig } from '@/composables/useRegle';

export const mapFilterFormInitialValues = (
  initialValues: ISettingsParsedFilterItem | null
): ISettingsFilterItemForm => ({
  query: {
    size: {
      value: null,
      direction: 'off',
      step: 'KB'
    },
    ...initialValues?.query
  },
  action: {
    spam: 'nothing',
    ...initialValues?.action
  },
  toggles: {
    canTargets: initialValues?.metaData.canTargets ?? false,
    canMoveTo: initialValues?.metaData.canMoveTo ?? false
  }
});

const parseSizeToNumber = (
  size: UnwrapRef<IFilterDetailsModel['size']['value']>,
  direction: UnwrapRef<IFilterDetailsModel['size']['direction']>,
  step: UnwrapRef<IFilterDetailsModel['size']['step']>
) => {
  if (!size && size !== 0) {
    return;
  }
  let number = typeof size === 'string' ? Number(size) : size;

  if (!Number.isFinite(number)) {
    return;
  }

  if (step === 'MB') {
    number = number * 1024 * 1024;
  } else if (step === 'KB') {
    number *= 1024;
  }

  if (direction === 'smaller') {
    number *= -1;
  }

  return number;
};

const firstError = (error: unknown): string | undefined => {
  if (Array.isArray(error)) {
    return error[0] as string | undefined;
  }
  if (typeof error === 'string') {
    return error;
  }
  return undefined;
};

const isEmailFormat = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

type INormalizedFilterForm = {
  query: {
    from: string | null;
    to: string | null;
    subject: string | null;
    listId: string | null;
    text: string | null;
    ha: boolean | null;
    size: {
      value: number | string | null;
      direction: 'off' | 'larger' | 'smaller';
      step: 'B' | 'KB' | 'MB';
    };
  };
  action: {
    seen: boolean | undefined;
    flag: boolean | undefined;
    delete: boolean | undefined;
    spam: 'nothing' | 'spam' | 'notspam';
    mailbox: string | null;
    targets: string[];
  };
  toggles: {
    canTargets: boolean;
    canMoveTo: boolean;
  };
};

const normalizeFormValues = (values: ISettingsFilterItemForm): INormalizedFilterForm => {
  return {
    query: {
      from: values.query.from ?? null,
      to: values.query.to ?? null,
      subject: values.query.subject ?? null,
      listId: values.query.listId ?? null,
      text: values.query.text ?? null,
      ha: values.query.ha ?? null,
      size: {
        value: values.query.size?.value ?? null,
        direction: values.query.size?.direction ?? 'off',
        step: values.query.size?.step ?? 'KB'
      }
    },
    action: {
      seen: values.action.seen ?? undefined,
      flag: values.action.flag ?? undefined,
      delete: values.action.delete ?? undefined,
      spam: values.action.spam ?? 'nothing',
      mailbox: values.action.mailbox ?? null,
      targets: Array.isArray(values.action.targets) ? values.action.targets : []
    },
    toggles: {
      canTargets: values.toggles.canTargets ?? false,
      canMoveTo: values.toggles.canMoveTo ?? false
    }
  };
};

const useEditDialog = (initialValues: ISettingsParsedFilterItem | null) => {
  const { t } = useI18n();

  const { createFilter, updateFilter } = useFilterService();
  const { mutateAsync: createFilterAsync, isPending: isCreatePending } = createFilter();
  const { mutateAsync: updateFilterAsync, isPending: isUpdatePending } = updateFilter();

  const isPending = computed(() => isCreatePending.value || isUpdatePending.value);

  const useRegle = useRegleConfig();
  const initialFormValues = mapFilterFormInitialValues(initialValues);
  const { r$ } = useRegle(initialFormValues, {
    query: {
      from: {},
      to: {},
      subject: {},
      listId: {},
      text: {},
      ha: {},
      size: {
        value: { numeric },
        direction: {},
        step: {}
      }
    },
    action: {
      seen: {},
      flag: {},
      delete: {},
      spam: {},
      mailbox: {},
      targets: { $each: {} }
    },
    toggles: {
      canTargets: {},
      canMoveTo: {}
    }
  });

  const targetsValidationError = computed(() => {
    const targets = normalizeFormValues(r$.$value).action.targets;
    if (!targets.length) {
      return undefined;
    }

    const hasInvalidTarget = targets.some((target) => {
      return !isValidUrlFormat(target) && !isEmailFormat(target);
    });

    return hasInvalidTarget ? t('settings.filters.form.errors.invalidEmail') : undefined;
  });

  const errors = computed(() => {
    const next: Record<string, string> = {};

    if (r$.query.size?.value.$error) {
      const message = firstError(r$.query.size.value.$errors);
      if (message) {
        next['query.size.value'] = message;
      }
    }

    if (targetsValidationError.value) {
      next['action.targets'] = targetsValidationError.value;
    }

    return next;
  });

  const metaDirty = computed(() => r$.$anyDirty);

  const updateModel = (updater: (draft: INormalizedFilterForm) => void) => {
    const next = normalizeFormValues(r$.$value);
    updater(next);
    r$.$value = next as unknown as ISettingsFilterItemForm;
  };

  const setValues = (values: ISettingsFilterItemForm, shouldValidate = true) => {
    r$.$value = normalizeFormValues(values) as unknown as ISettingsFilterItemForm;

    if (!shouldValidate) {
      r$.$reset();
      return;
    }

    void r$.$validate();
  };

  const resetForm = () => {
    setValues(initialFormValues, false);
  };

  const from = computed({
    get: () => normalizeFormValues(r$.$value).query.from,
    set: (value) => {
      updateModel((draft) => {
        draft.query.from = value;
      });
    }
  });
  const to = computed({
    get: () => normalizeFormValues(r$.$value).query.to,
    set: (value) => {
      updateModel((draft) => {
        draft.query.to = value;
      });
    }
  });
  const subject = computed({
    get: () => normalizeFormValues(r$.$value).query.subject,
    set: (value) => {
      updateModel((draft) => {
        draft.query.subject = value;
      });
    }
  });
  const listId = computed({
    get: () => normalizeFormValues(r$.$value).query.listId,
    set: (value) => {
      updateModel((draft) => {
        draft.query.listId = value;
      });
    }
  });
  const text = computed({
    get: () => normalizeFormValues(r$.$value).query.text,
    set: (value) => {
      updateModel((draft) => {
        draft.query.text = value;
      });
    }
  });
  const attachments = computed({
    get: () => normalizeFormValues(r$.$value).query.ha,
    set: (value) => {
      updateModel((draft) => {
        draft.query.ha = value;
      });
    }
  });
  const size = computed({
    get: () => {
      const value = normalizeFormValues(r$.$value).query.size.value;
      return typeof value === 'string' ? Number(value) : value;
    },
    set: (value) => {
      updateModel((draft) => {
        draft.query.size.value = value ?? null;
      });
    }
  });
  const sizeDirection = computed({
    get: () => normalizeFormValues(r$.$value).query.size.direction,
    set: (value) => {
      updateModel((draft) => {
        draft.query.size.direction = value;
      });
    }
  });
  const sizeStep = computed({
    get: () => normalizeFormValues(r$.$value).query.size.step,
    set: (value) => {
      updateModel((draft) => {
        draft.query.size.step = value;
      });
    }
  });

  const seen = computed({
    get: () => normalizeFormValues(r$.$value).action.seen,
    set: (value) => {
      updateModel((draft) => {
        draft.action.seen = value;
      });
    }
  });
  const flag = computed({
    get: () => normalizeFormValues(r$.$value).action.flag,
    set: (value) => {
      updateModel((draft) => {
        draft.action.flag = value;
      });
    }
  });
  const deleteValue = computed({
    get: () => normalizeFormValues(r$.$value).action.delete,
    set: (value) => {
      updateModel((draft) => {
        draft.action.delete = value;
      });
    }
  });
  const spam = computed({
    get: () => normalizeFormValues(r$.$value).action.spam,
    set: (value) => {
      updateModel((draft) => {
        draft.action.spam = value;
      });
    }
  });
  const mailbox = computed({
    get: () => normalizeFormValues(r$.$value).action.mailbox,
    set: (value) => {
      updateModel((draft) => {
        draft.action.mailbox = value;
      });
    }
  });
  const targets = computed({
    get: () => normalizeFormValues(r$.$value).action.targets,
    set: (value) => {
      updateModel((draft) => {
        draft.action.targets = Array.isArray(value) ? value : [];
      });
    }
  });
  const canMoveTo = computed({
    get: () => normalizeFormValues(r$.$value).toggles.canMoveTo,
    set: (value) => {
      updateModel((draft) => {
        draft.toggles.canMoveTo = value;
      });
    }
  });
  const canTargets = computed({
    get: () => normalizeFormValues(r$.$value).toggles.canTargets,
    set: (value) => {
      updateModel((draft) => {
        draft.toggles.canTargets = value;
      });
    }
  });

  const handleSubmitFilter = async () => {
    const { valid, data } = await r$.$validate();
    if (!valid) {
      return false;
    }

    const normalizedData = normalizeFormValues(data as ISettingsFilterItemForm);
    const normalizedSizeValue =
      typeof normalizedData.query.size.value === 'string'
        ? Number(normalizedData.query.size.value)
        : normalizedData.query.size.value;

    try {
      if (initialValues?.id) {
        await updateFilterAsync({
          ...normalizedData,
          query: {
            ...normalizedData.query,
            size: parseSizeToNumber(
              normalizedSizeValue,
              normalizedData.query.size.direction,
              normalizedData.query.size.step
            )
          },
          id: initialValues.id
        });
      } else {
        await createFilterAsync({
          ...normalizedData,
          query: {
            ...normalizedData.query,
            size: parseSizeToNumber(
              normalizedSizeValue,
              normalizedData.query.size.direction,
              normalizedData.query.size.step
            )
          }
        });
      }
      return true;
    } catch (error) {
      console.debug('Failed to submit filter', error);
      return false;
    }
  };

  return {
    resetForm,
    setValues,
    errors,
    isPending,
    metaDirty,
    query: {
      from,
      to,
      subject,
      text,
      listId,
      size: {
        value: size,
        direction: sizeDirection,
        step: sizeStep
      },
      ha: attachments
    } satisfies IFilterDetailsModel,
    actions: {
      seen,
      flag,
      delete: deleteValue,
      spam,
      mailbox,
      targets,
      toggles: {
        canMoveTo,
        canTargets
      }
    } satisfies IFilterActionsModel,
    handleSubmitFilter
  };
};

export default useEditDialog;
